import React from 'react';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'clsx';

import { BannerLayout, BannerStatus } from './banner-enums';
import style from './banner-message-v2.module.less';
import { messages } from './messages';
import { IconButton } from '../button/icon-button';

/**
 * @typedef {import('./banner-message-v2-types.d.ts').BannerMessageV2Props} BannerMessageV2Props
 */

/**
 * @typedef {import('./banner-message-v2-types.d.ts').BannerMessageV2Component} BannerMessageV2Component
 */

/**
 * @typedef {import('./banner-message-v2-types.d.ts').IconsType} IconsType
 */

/**
 * @typedef {import('./banner-message-v2-types.d.ts').BannerLayoutClassNamesType} BannerLayoutClassNamesType
 */

/**
 * @typedef {import('./banner-message-v2-types.d.ts').StatusClassNamesType} StatusClassNamesType
 */

/**
 * @type {IconsType}
 */
const icons = {
  [BannerStatus.Info]: <FontAwesomeIcon icon={faInfoCircle} />,
  [BannerStatus.Warning]: <FontAwesomeIcon icon={faExclamationTriangle} />,
  [BannerStatus.Error]: <FontAwesomeIcon icon={faExclamationCircle} />,
  [BannerStatus.Featured]: null,
  [BannerStatus.Success]: <FontAwesomeIcon icon={faCheckCircle} />,
};

/**
 * @type {BannerLayoutClassNamesType}
 */
const bannerLayoutClassNames = {
  [BannerLayout.Full]: style['full-banner'],
  [BannerLayout.Default]: style['default-banner'],
  [BannerLayout.Inline]: style['inline-banner'],
  [BannerLayout.Tiny]: style['tiny-banner'],
};

/**
 * @type {StatusClassNamesType}
 */
const statusClassNames = {
  [BannerStatus.Info]: style['info-banner'],
  [BannerStatus.Warning]: style['warning-banner'],
  [BannerStatus.Error]: style['error-banner'],
  [BannerStatus.Featured]: style['featured-banner'],
  [BannerStatus.Success]: style['success-banner'],
};

/**
 * @deprecated Use `AlertBanner` from `@shipstation/client-common/components/alert-banner` instead.
 * See: ui-components/readme-documentation/MIGRATION.md
 * @type {BannerMessageV2Component}
 */
export const BannerMessageV2 = ({
  layout = BannerLayout.Default,
  status,
  onClose,
  customIcon,
  className,
  header,
  content,
  buttons,
  hideIcon = false,
  bannerContainerClassName,
}) => {
  const icon = customIcon || icons[status];
  const bannerLayoutClassName = bannerLayoutClassNames[layout];
  const statusClassName = statusClassNames[status];

  return (
    <div
      className={classnames(
        style['base-banner'],
        bannerLayoutClassName,
        statusClassName,
        { [style['has-close-button']]: !!onClose },
        className,
      )}
    >
      <div className={classnames(style['banner-content-container'], bannerContainerClassName)}>
        {!hideIcon && <div className={style.icon}>{icon}</div>}
        <div
          className={classnames(style['banner-content'], {
            [style['content-with-divider']]: layout === BannerLayout.Full,
          })}
        >
          {header}
          {content}
          {buttons ? <div className={style['buttons-container']}>{buttons}</div> : null}
        </div>
      </div>
      {onClose ? (
        <IconButton ariaLabelMessageDescriptor={messages.closeBanner} className={style['close-button']} icon={faTimes} onClick={onClose} />
      ) : null}
    </div>
  );
};
